import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Tag } from '../ui'
import {
  DemoHeader,
  InsightCard,
  OntologyResponsePanel,
  UserActionPanel,
} from './GoalDrivenDemo'
import { useT } from '../../lib/i18n'
import type { LabDemoProps } from './types'
import type { QuestionGroundedLocale } from '../../lib/locales'

type Question = QuestionGroundedLocale['questions'][number]
type Entity = Question['entities'][number]

const KIND_ACCENT: Record<Entity['kind'], string> = {
  goal: 'var(--color-orion-amber)',
  object: 'var(--color-orion-violet)',
  inference: 'var(--color-orion-cyan)',
  constraint: 'var(--color-orion-warn)',
  candidate: 'var(--color-orion-info)',
  action: 'var(--color-orion-pass)',
}

const ENTITY_REGEX = /\[\[entity:([^|]+)\|([^\]]+)\]\]/g

export function QuestionGroundedDemo({ paradigm, lab, data }: LabDemoProps) {
  const t = useT()
  const demo = (data as typeof t.labDemos.questionGrounded | undefined) ?? t.labDemos.questionGrounded
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null)
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null)

  const selectedQuestion = useMemo(
    () =>
      selectedQuestionId
        ? demo.questions.find((q) => q.id === selectedQuestionId) ?? null
        : null,
    [selectedQuestionId, demo.questions],
  )

  const selectedEntity = useMemo(() => {
    if (!selectedQuestion || !selectedEntityId) return null
    return selectedQuestion.entities.find((e) => e.id === selectedEntityId) ?? null
  }, [selectedQuestion, selectedEntityId])

  const entityMap = useMemo(() => {
    const map = new Map<string, Entity>()
    selectedQuestion?.entities.forEach((e) => map.set(e.id, e))
    return map
  }, [selectedQuestion])

  return (
    <div className="space-y-5">
      <DemoHeader paradigm={paradigm} />

      <div className="flex flex-col gap-4">
        <UserActionPanel
          label={lab.userActionLabel}
          title={demo.questionsLabel}
          hint={demo.questionsHint}
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {demo.questions.map((q) => {
              const active = q.id === selectedQuestionId
              return (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => {
                    setSelectedQuestionId(q.id)
                    setSelectedEntityId(null)
                  }}
                  aria-pressed={active}
                  className={`text-left px-3 py-2.5 rounded-[var(--radius-sm)] transition-colors leading-snug ${
                    active
                      ? 'bg-[color:var(--color-orion-amber)]/15 border-l-2 border-[color:var(--color-orion-amber)]'
                      : 'border-l-2 border-transparent border-faint hover:bg-[color:var(--color-orion-deep)]/40'
                  }`}
                >
                  <span
                    className={`text-[length:var(--text-small)] ${
                      active
                        ? 'text-[color:var(--color-orion-text)]'
                        : 'text-[color:var(--color-orion-haze)]'
                    }`}
                  >
                    {q.question}
                  </span>
                </button>
              )
            })}
          </div>
        </UserActionPanel>

        <OntologyResponsePanel label={lab.ontologyResponseLabel}>
          {selectedQuestion ? (
            <div className="grid lg:grid-cols-[1fr_320px] gap-4">
              <div className="border-faint rounded-[var(--radius-md)] p-5 bg-[color:var(--color-orion-deep)]/30">
                <p
                  className="text-[color:var(--color-orion-text)]"
                  style={{ fontSize: 'var(--text-body)', lineHeight: 1.75 }}
                >
                  {renderAnswer(selectedQuestion.answer, entityMap, selectedEntityId, (id) =>
                    setSelectedEntityId((curr) => (curr === id ? null : id)),
                  )}
                </p>
              </div>

              <EntityDetailPanel
                label={demo.entityDetailLabel}
                kindLabels={t.l3.kindLabels}
                entity={selectedEntity}
              />
            </div>
          ) : (
            <EmptyState title={demo.emptyStateTitle} body={demo.emptyStateBody} />
          )}
        </OntologyResponsePanel>
      </div>

      <InsightCard title={demo.insightTitle} body={demo.insightBody} />
    </div>
  )
}

function renderAnswer(
  answer: string,
  entityMap: Map<string, Entity>,
  selectedEntityId: string | null,
  onClick: (id: string) => void,
): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  ENTITY_REGEX.lastIndex = 0
  while ((match = ENTITY_REGEX.exec(answer)) !== null) {
    const [full, entityId, label] = match
    if (match.index > lastIndex) {
      parts.push(<span key={`t-${lastIndex}`}>{answer.slice(lastIndex, match.index)}</span>)
    }
    const ent = entityMap.get(entityId)
    parts.push(
      <EntityBadge
        key={`e-${entityId}-${match.index}`}
        entityId={entityId}
        label={label}
        kind={ent?.kind}
        active={entityId === selectedEntityId}
        onClick={onClick}
      />,
    )
    lastIndex = match.index + full.length
  }
  if (lastIndex < answer.length) {
    parts.push(<span key={`t-${lastIndex}`}>{answer.slice(lastIndex)}</span>)
  }
  return parts
}

function EntityBadge({
  entityId,
  label,
  kind,
  active,
  onClick,
}: {
  entityId: string
  label: string
  kind?: Entity['kind']
  active: boolean
  onClick: (id: string) => void
}) {
  const accent = kind ? KIND_ACCENT[kind] : 'var(--color-orion-text)'
  return (
    <button
      type="button"
      onClick={() => onClick(entityId)}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[var(--radius-xs)] mx-0.5 align-baseline transition-colors"
      style={{
        background: active
          ? `color-mix(in oklch, ${accent} 22%, transparent)`
          : `color-mix(in oklch, ${accent} 10%, transparent)`,
        borderBottom: `1px dashed color-mix(in oklch, ${accent} 70%, transparent)`,
        color: accent,
        fontSize: '0.95em',
      }}
    >
      {label}
    </button>
  )
}

function EntityDetailPanel({
  label,
  kindLabels,
  entity,
}: {
  label: string
  kindLabels: Record<string, string>
  entity: Entity | null
}) {
  return (
    <aside
      className="border-faint rounded-[var(--radius-md)] p-4 min-h-[200px] flex flex-col"
      style={{ borderLeft: '2px solid color-mix(in oklch, var(--color-orion-amber) 60%, transparent)' }}
    >
      <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-3">
        {label}
      </div>
      <AnimatePresence mode="wait">
        {entity ? (
          <motion.div
            key={entity.id}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
            className="flex-1 flex flex-col gap-2"
          >
            <div className="flex items-baseline gap-2 flex-wrap">
              <span
                className="mono text-[10px] uppercase tracking-widest"
                style={{ color: KIND_ACCENT[entity.kind] }}
              >
                {kindLabels[entity.kind] ?? entity.kind}
              </span>
              <span
                className="text-[color:var(--color-orion-text)] font-medium"
                style={{ fontSize: 'var(--text-body)' }}
              >
                {entity.label}
              </span>
            </div>
            <p
              className="text-[color:var(--color-orion-haze)]"
              style={{ fontSize: 'var(--text-small)', lineHeight: 1.55 }}
            >
              {entity.detail}
            </p>
            {entity.citation && (
              <Tag kind="standard" className="text-[length:var(--text-micro)] self-start">
                {entity.citation}
              </Tag>
            )}
          </motion.div>
        ) : (
          <p
            className="text-[color:var(--color-orion-haze)] flex-1 flex items-center"
            style={{ fontSize: 'var(--text-small)' }}
          >
            ⤴ Click any bracketed entity in the answer.
          </p>
        )}
      </AnimatePresence>
    </aside>
  )
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="min-h-[200px] flex flex-col items-center justify-center text-center px-6">
      <div className="mono text-[length:var(--text-small)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
        {title}
      </div>
      <p
        className="text-[color:var(--color-orion-haze)] max-w-md"
        style={{ fontSize: 'var(--text-small)', lineHeight: 1.6 }}
      >
        {body}
      </p>
    </div>
  )
}
